<script setup lang="ts">
/**
 * The bell of a shell or module header: an icon button with an unread badge
 * („99+“ from a hundred on) that opens a popover with the latest items as
 * `NxFeedItem` rows, a „Alle gelesen“ action and loading, error and empty
 * states.
 *
 * Data stays with the caller: pass `items` (and `unreadCount` when the server
 * knows more than the page shows), listen to `open` (an item was chosen — the
 * panel closes itself unless `keepOpenOnSelect`), `mark-all` and `retry`.
 * `v-model:open` tells when the panel opens, e.g. to refresh the list.
 *
 * The trigger inherits the text colour, so it fits a coloured navbar; set
 * `--bell-ring` there to the navbar colour to ring the badge in it.
 * The popover holds an `NxNotificationPanel` (the content on its own, for a
 * host with its own trigger and popover): arrow keys walk its rows; Escape
 * closes and returns focus.
 */
import { computed, nextTick, ref, watch } from 'vue';
import Bell from 'lucide-vue-next/dist/esm/icons/bell.js';
import NxPopover from '../Popover/NxPopover.vue';
import type { NxPopoverSide } from '../Popover/NxPopover.vue';
import NxNotificationPanel from './NxNotificationPanel.vue';
import type { NxFeedEntry } from './types';

const props = withDefaults(
  defineProps<{
    items?: readonly NxFeedEntry[];
    /** Unread total; default: the unread rows in `items`. */
    unreadCount?: number;
    loading?: boolean;
    /** An error text; shown with a retry button while there are no items. */
    error?: string;
    /** Controlled open state (`v-model:open`). */
    open?: boolean;
    /** Keep the panel open after a row was chosen. */
    keepOpenOnSelect?: boolean;
    side?: NxPopoverSide;
    /** Relative-time locale of the rows; default: the app's. */
    locale?: string;
    label?: string;
    markAllLabel?: string;
    loadingLabel?: string;
    retryLabel?: string;
    emptyTitle?: string;
    emptyHint?: string;
    /** Accessible name of the badge count. */
    unreadLabel?: (count: number) => string;
  }>(),
  {
    items: () => [],
    unreadCount: undefined,
    loading: false,
    error: undefined,
    open: undefined,
    keepOpenOnSelect: false,
    side: 'bottom-end',
    locale: undefined,
    label: 'Benachrichtigungen',
    markAllLabel: 'Alle gelesen',
    loadingLabel: 'Lädt …',
    retryLabel: 'Erneut versuchen',
    emptyTitle: 'Alles gelesen',
    emptyHint: 'Neue Erwähnungen und Hinweise erscheinen hier.',
    unreadLabel: (count: number) => (count === 1 ? '1 ungelesen' : `${count} ungelesen`),
  }
);

const emit = defineEmits<{
  'update:open': [open: boolean];
  /** A row was chosen. */
  open: [item: NxFeedEntry, event: MouseEvent];
  'mark-all': [];
  retry: [];
}>();

defineSlots<{
  /** Replaces the bell icon. */
  icon?(): unknown;
  /** Replaces one row; gets the item and a `close()`. */
  item?(props: { item: NxFeedEntry; close: () => void }): unknown;
  /** Row actions of each row, on hover and focus (see `NxFeedItem#actions`). */
  'item-actions'?(props: { item: NxFeedEntry }): unknown;
  /** Always visible actions of each row (see `NxFeedItem#primary-actions`). */
  'item-primary-actions'?(props: { item: NxFeedEntry }): unknown;
  /** Extra header buttons next to „Alle gelesen“. */
  'header-actions'?(): unknown;
  /** Replaces the empty state. */
  empty?(): unknown;
  /** Under the list, e.g. „Alle anzeigen“; gets `close()`. */
  footer?(props: { close: () => void }): unknown;
}>();

const panel = ref<InstanceType<typeof NxNotificationPanel> | null>(null);

const localOpen = ref(props.open ?? false);
watch(
  () => props.open,
  (v) => {
    if (v !== undefined) localOpen.value = v;
  }
);
const isOpen = computed(() => props.open ?? localOpen.value);

function setOpen(next: boolean) {
  if (props.open === undefined) localOpen.value = next;
  emit('update:open', next);
}
function close() {
  setOpen(false);
}

const unread = computed(
  () => props.unreadCount ?? props.items.reduce((n, item) => n + (item.unread ? 1 : 0), 0)
);
const badge = computed(() =>
  unread.value > 0 ? (unread.value > 99 ? '99+' : String(unread.value)) : ''
);
const triggerLabel = computed(() =>
  unread.value > 0 ? `${props.label}, ${props.unreadLabel(unread.value)}` : props.label
);

function choose(item: NxFeedEntry, event: MouseEvent) {
  emit('open', item, event);
  if (!props.keepOpenOnSelect) close();
}

watch(isOpen, (open) => {
  if (!open) return;
  void nextTick(() => panel.value?.focus({ preventScroll: true }));
});
</script>

<template>
  <span class="nx-notification-bell" data-testid="nx-notification-bell">
    <NxPopover :open="isOpen" :side="side" :offset="6" lazy @update:open="setOpen">
      <template #trigger>
        <button
          type="button"
          class="nx-notification-bell__trigger"
          :class="{ 'is-open': isOpen }"
          :aria-label="triggerLabel"
          :aria-expanded="isOpen"
          aria-haspopup="dialog"
          data-testid="nx-notification-bell-trigger"
        >
          <slot name="icon">
            <Bell class="nxt-icon-lg" :stroke-width="2" aria-hidden="true" />
          </slot>
          <span v-if="badge" class="nx-notification-bell__badge" aria-hidden="true">
            {{ badge }}
          </span>
        </button>
      </template>

      <NxNotificationPanel
        ref="panel"
        :items="items"
        :unread-count="unread"
        :loading="loading"
        :error="error"
        :locale="locale"
        :label="label"
        :mark-all-label="markAllLabel"
        :loading-label="loadingLabel"
        :retry-label="retryLabel"
        :empty-title="emptyTitle"
        :empty-hint="emptyHint"
        @open="choose"
        @mark-all="emit('mark-all')"
        @retry="emit('retry')"
      >
        <template v-if="$slots.item" #item="{ item }">
          <slot name="item" :item="item" :close="close" />
        </template>
        <template v-if="$slots['item-actions']" #item-actions="{ item }">
          <slot name="item-actions" :item="item" />
        </template>
        <template v-if="$slots['item-primary-actions']" #item-primary-actions="{ item }">
          <slot name="item-primary-actions" :item="item" />
        </template>
        <template v-if="$slots['header-actions']" #header-actions>
          <slot name="header-actions" />
        </template>
        <template v-if="$slots.empty" #empty>
          <slot name="empty" />
        </template>
        <template v-if="$slots.footer" #footer>
          <slot name="footer" :close="close" />
        </template>
      </NxNotificationPanel>
    </NxPopover>
  </span>
</template>

<style scoped>
.nx-notification-bell {
  display: inline-flex;
  align-items: center;
}

.nx-notification-bell__trigger {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: var(--nxt-control-size-lg);
  height: var(--nxt-control-size-lg);
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  color: inherit;
  cursor: pointer;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-notification-bell__trigger:hover,
.nx-notification-bell__trigger.is-open {
  background: color-mix(in srgb, currentColor 12%, transparent);
}
.nx-notification-bell__trigger:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}
.nx-notification-bell__badge {
  position: absolute;
  inset-block-start: calc(var(--nxt-space-2xs) / 2);
  inset-inline-end: calc(var(--nxt-space-2xs) / 2);
  display: inline-grid;
  place-items: center;
  min-width: calc(var(--nxt-size-unit) * 1);
  height: calc(var(--nxt-size-unit) * 1);
  padding-inline: calc(var(--nxt-space-2xs) * 0.75);
  box-sizing: border-box;
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-danger-bg);
  color: var(--nxt-danger-fg);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--bell-ring, var(--nxt-surface-bg));
  font-size: calc(var(--nxt-font-size-xs) * 0.85);
  font-weight: var(--nxt-font-weight-bold);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  animation: nx-bell-badge-in var(--nxt-motion-base) var(--nxt-ease-out) both;
}

.nx-notification-bell :deep(.nx-popover-panel) {
  width: min(var(--nxt-panel-width-2xl), calc(100vw - var(--nxt-space-lg)));
  padding: 0;
  border-radius: var(--nxt-radius-xl);
  overflow: hidden;
}

@keyframes nx-bell-badge-in {
  from {
    opacity: 0;
    transform: scale(0.4);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-notification-bell__trigger {
    transition: none;
  }
  .nx-notification-bell__badge {
    animation: none;
  }
}
</style>
